import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getPendingInvoices, updateInvoiceStatus, getClientById } from '../firebase/database';
import { generateRevenuesInvoicePDF, openPDFInNewTab } from '../services/invoicePdfService';
import { LogisticsInvoice, SaleItem } from '../types';
import { roundMoney, addMoney, mulMoney, subMoney, formatCurrencyWithConversion } from '../utils/number';
import toast, { Toaster } from 'react-hot-toast';

const PendingApprovals: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [pendingInvoices, setPendingInvoices] = useState<LogisticsInvoice[]>([]);
  const [selectedInvoice, setSelectedInvoice] = useState<LogisticsInvoice | null>(null);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);

  // Calculate real-time totals for the selected invoice
  const calculateRealtimeTotals = (invoice: LogisticsInvoice | null) => {
    if (!invoice) return { subtotal: 0, total: 0, costTotal: 0, profit: 0 };
    
    // Use sale items for calculations
    const itemsToUse = invoice.saleItems || [];
    
    // Calculate subtotal in PKR
    const subtotalPKR = itemsToUse.reduce((sum, item) => {
      if (item.currency !== 'PKR') {
        return addMoney(sum, mulMoney(item.amount, invoice.exchangeRate));
      }
      return addMoney(sum, item.amount);
    }, 0);
    
    // Calculate cost total in PKR
    const costTotalPKR = invoice.costItems ? invoice.costItems.reduce((sum, item) => {
      if (item.currency !== 'PKR') {
        return addMoney(sum, mulMoney(item.amount, invoice.exchangeRate));
      }
      return addMoney(sum, item.amount);
    }, 0) : 0;
    
    const subtotal = roundMoney(subtotalPKR);
    const costTotal = roundMoney(costTotalPKR);
    const profit = subMoney(subtotal, costTotal);
    const total = subMoney(subtotal, invoice.discount || 0);
    
    return { subtotal, total, costTotal, profit };
  };

  const realtimeTotals = calculateRealtimeTotals(selectedInvoice);

  // Load pending invoices on component mount
  useEffect(() => {
    loadPendingInvoices();
  }, []);

  const loadPendingInvoices = async () => {
    try {
      setLoading(true);
      const invoices = await getPendingInvoices();
      setPendingInvoices(invoices);
    } catch (error) {
      console.error('Error loading pending invoices:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (invoice: LogisticsInvoice) => {
    setIsProcessing(true);
    try {
      // Update the invoice with any changes made to rates
      const updatedInvoice = selectedInvoice || invoice;
      
      // Recalculate totals based on updated sale items using proper money functions
      const itemsToUse = updatedInvoice.saleItems || [];
      const subtotalPKR = itemsToUse.reduce((sum, item) => {
        if (item.currency !== 'PKR') {
          return addMoney(sum, mulMoney(item.amount, updatedInvoice.exchangeRate));
        }
        return addMoney(sum, item.amount);
      }, 0);
      const subtotal = roundMoney(subtotalPKR);
      const total = subMoney(subtotal, updatedInvoice.discount || 0);
      
      const finalInvoice = {
        ...updatedInvoice,
        subtotal,
        total
      };
      
      await updateInvoiceStatus(finalInvoice.id, 'approved', currentUser?.id, finalInvoice);
      
      setPendingInvoices(prev => prev.filter(inv => inv.id !== invoice.id));
      setSelectedInvoice(null);
      toast.success('Invoice approved successfully!');
    } catch (error) {
      console.error('Error approving invoice:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      console.error('Error details:', errorMessage);
      toast.error(`Error approving invoice: ${errorMessage}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReject = async (invoice: LogisticsInvoice) => {
    setIsProcessing(true);
    try {
      await updateInvoiceStatus(invoice.id, 'rejected', currentUser?.id);
      setPendingInvoices(prev => prev.filter(inv => inv.id !== invoice.id));
      setSelectedInvoice(null);
      toast.success('Invoice rejected successfully!');
    } catch (error) {
      console.error('Error rejecting invoice:', error);
      toast.error('Error rejecting invoice. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleAmend = (invoice: LogisticsInvoice) => {
    navigate(`/edit-invoice/${invoice.id}`);
  };

  const handleViewPDF = async (invoice: LogisticsInvoice) => {
    setIsGeneratingPDF(true);
    try {
      // Get client address
      const client = await getClientById(invoice.clientId);
      const clientAddress = client?.address;
      
      const pdf = await generateRevenuesInvoicePDF(invoice, clientAddress);
      openPDFInNewTab(pdf);
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Error generating PDF. Please try again.');
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'approved': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';

      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading pending invoices...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 rounded-lg">
      <Toaster />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Pending Invoice Approvals</h1>
              <p className="mt-2 text-gray-600">Review and approve pending invoices</p>
            </div>
            <button
              onClick={() => navigate('/dashboard')}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Back to Dashboard
            </button>
          </div>
        </div>

        {pendingInvoices.length === 0 ? (
          <div className="bg-white shadow rounded-lg p-8 text-center">
            <div className="text-gray-400 mb-4">
              <svg className="mx-auto h-12 w-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Pending Invoices</h3>
            <p className="text-gray-600">All invoices have been processed.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Invoice List */}
            <div className="lg:col-span-1">
              <div className="bg-white shadow rounded-lg">
                <div className="px-4 py-5 sm:p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    Pending Invoices ({pendingInvoices.length})
                  </h3>
                  <div className="space-y-3">
                    {pendingInvoices.map((invoice) => (
                      <div
                        key={invoice.id}
                        onClick={() => setSelectedInvoice(invoice)}
                        className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                          selectedInvoice?.id === invoice.id
                            ? 'border-indigo-500 bg-indigo-50'
                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-medium text-gray-900">{invoice.invoiceNumber}</h4>
                            <p className="text-sm text-gray-600">{invoice.clientName}</p>
                            <p className="text-xs text-gray-500">{formatDate(invoice.date)}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-medium text-gray-900">
                              {invoice.currency} {invoice.total.toFixed(2)}
                            </p>
                            <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(invoice.status)}`}>
                              {invoice.status}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Invoice Details */}
            <div className="lg:col-span-2">
              {selectedInvoice ? (
                <div className="bg-white shadow rounded-lg">
                  <div className="px-4 py-5 sm:p-6">
                    <div className="flex items-center justify-between mb-6">
                      <h3 className="text-lg font-medium text-gray-900">
                        Invoice Details - {selectedInvoice.invoiceNumber}
                      </h3>
                      <button
                        onClick={() => handleViewPDF(selectedInvoice)}
                        disabled={isGeneratingPDF}
                        className="px-3 py-1 text-sm font-medium text-indigo-600 bg-indigo-50 border border-indigo-200 rounded-md hover:bg-indigo-100 disabled:opacity-50"
                      >
                        {isGeneratingPDF ? 'Generating...' : 'View PDF'}
                      </button>
                    </div>

                    {/* Basic Information */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                      <div>
                        <h4 className="font-medium text-gray-900 mb-3">Invoice Information</h4>
                        <dl className="space-y-2">
                          <div>
                            <dt className="text-sm font-medium text-gray-500">Invoice Number</dt>
                            <dd className="text-sm text-gray-900">{selectedInvoice.invoiceNumber}</dd>
                          </div>
                          <div>
                            <dt className="text-sm font-medium text-gray-500">Date</dt>
                            <dd className="text-sm text-gray-900">{formatDate(selectedInvoice.date)}</dd>
                          </div>
                        </dl>
                      </div>

                      {/* Client Information */}
                      <div className="mb-6">
                        <h4 className="font-medium text-gray-900 mb-3">Client Information</h4>
                        <dl className="space-y-2">
                          <div>
                            <dt className="text-sm font-medium text-gray-500">Client Name</dt>
                            <dd className="text-sm text-gray-900">{selectedInvoice.clientName}</dd>
                          </div>
                          <div>
                            <dt className="text-sm font-medium text-gray-500">Invoice Type</dt>
                            <dd className="text-sm text-gray-900 capitalize">{selectedInvoice.invoiceType}</dd>
                          </div>
                          <div>
                            <dt className="text-sm font-medium text-gray-500">Shipping Method</dt>
                            <dd className="text-sm text-gray-900 capitalize">{selectedInvoice.shippingMethod}</dd>
                          </div>
                        </dl>
                      </div>

                      {/* Logistics Company Details */}
                      <div className="mb-6">
                        <h4 className="font-medium text-gray-900 mb-3">Logistics Company</h4>
                        <dl className="space-y-2">
                          <div>
                            <dt className="text-sm font-medium text-gray-500">Company Name</dt>
                            <dd className="text-sm text-gray-900">{selectedInvoice.logisticsCompany}</dd>
                          </div>
                          <div>
                            <dt className="text-sm font-medium text-gray-500">Phone</dt>
                            <dd className="text-sm text-gray-900">Tel 041-5555555</dd>
                          </div>
                          <div>
                            <dt className="text-sm font-medium text-gray-500">Address</dt>
                            <dd className="text-sm text-gray-900">
                              {selectedInvoice.logisticsCompany === 'VECTOR LOGISTICS' ? 'Office M-7, M Floor, Khan Arcade, Firdous Market, Gulberg-III, Lahore' : '36-P New Fruit & Vegetable Market, SHAH RUKN-E-ALAM Colony, Multan'}
                            </dd>
                          </div>
                        </dl>
                      </div>
                    </div>

                    {/* Shipment Details */}
                    <div className="mb-6">
                      <h4 className="font-medium text-gray-900 mb-3">Shipment Details</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <dt className="text-sm font-medium text-gray-500">Shipper</dt>
                          <dd className="text-sm text-gray-900">{selectedInvoice.shipperName}</dd>
                        </div>
                        <div>
                          <dt className="text-sm font-medium text-gray-500">Consignee</dt>
                          <dd className="text-sm text-gray-900">{selectedInvoice.consignee}</dd>
                        </div>
                        
                        {/* Air shipping fields */}
                        {selectedInvoice.shippingMethod === 'air' && (
                          <>
                            <div>
                              <dt className="text-sm font-medium text-gray-500">Departure Airport</dt>
                              <dd className="text-sm text-gray-900">{selectedInvoice.airportOfDeparture || 'N/A'}</dd>
                            </div>
                            <div>
                              <dt className="text-sm font-medium text-gray-500">Destination Airport</dt>
                              <dd className="text-sm text-gray-900">{selectedInvoice.airportOfDestination || 'N/A'}</dd>
                            </div>
                            <div>
                              <dt className="text-sm font-medium text-gray-500">AWB Number</dt>
                              <dd className="text-sm text-gray-900">{selectedInvoice.awbNumber || 'N/A'}</dd>
                            </div>
                            <div>
                              <dt className="text-sm font-medium text-gray-500">Flight No</dt>
                              <dd className="text-sm text-gray-900">{selectedInvoice.flightNo || 'N/A'}</dd>
                            </div>
                            <div>
                              <dt className="text-sm font-medium text-gray-500">Airline</dt>
                              <dd className="text-sm text-gray-900">{selectedInvoice.airlineName || 'N/A'}</dd>
                            </div>
                            <div>
                              <dt className="text-sm font-medium text-gray-500">Weight</dt>
                              <dd className="text-sm text-gray-900">{selectedInvoice.gwWeight || 'N/A'}</dd>
                            </div>
                            <div>
                              <dt className="text-sm font-medium text-gray-500">Package Count</dt>
                              <dd className="text-sm text-gray-900">
                                {Array.isArray(selectedInvoice.packageCount) ? (
                                  <div className="space-y-0.5">
                                    {selectedInvoice.packageCount.map((v, i) => (
                                      <div key={i}>{v}</div>
                                    ))}
                                  </div>
                                ) : (selectedInvoice.packageCount || 'N/A')}
                              </dd>
                            </div>
                          </>
                        )}

                        {/* Sea shipping fields */}
                        {selectedInvoice.shippingMethod === 'sea' && (
                          <>
                            <div>
                              <dt className="text-sm font-medium text-gray-500">Port of Loading</dt>
                              <dd className="text-sm text-gray-900">{selectedInvoice.portOfLoading || 'N/A'}</dd>
                            </div>
                            <div>
                              <dt className="text-sm font-medium text-gray-500">Port of Discharge</dt>
                              <dd className="text-sm text-gray-900">{selectedInvoice.portOfDischarge || 'N/A'}</dd>
                            </div>
                            <div>
                              <dt className="text-sm font-medium text-gray-500">BL Number</dt>
                              <dd className="text-sm text-gray-900">{selectedInvoice.blNumber || 'N/A'}</dd>
                            </div>
                            <div>
                              <dt className="text-sm font-medium text-gray-500">Shipping Line</dt>
                              <dd className="text-sm text-gray-900">{selectedInvoice.shippingLine || 'N/A'}</dd>
                            </div>
                            <div>
                              <dt className="text-sm font-medium text-gray-500">Vessel/Voyage</dt>
                              <dd className="text-sm text-gray-900">{selectedInvoice.vesselVoyage || 'N/A'}</dd>
                            </div>
                            <div>
                              <dt className="text-sm font-medium text-gray-500">Container Quantity</dt>
                              <dd className="text-sm text-gray-900">{selectedInvoice.containerQuantity || 'N/A'}</dd>
                            </div>
                            <div>
                              <dt className="text-sm font-medium text-gray-500">Container Number</dt>
                              <dd className="text-sm text-gray-900">
                                {Array.isArray(selectedInvoice.containerNumber) ? (
                                  <div className="space-y-0.5">
                                    {selectedInvoice.containerNumber.map((v, i) => (
                                      <div key={i}>{v}</div>
                                    ))}
                                  </div>
                                ) : (selectedInvoice.containerNumber || 'N/A')}
                              </dd>
                            </div>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Cost Items */}
                    {selectedInvoice.costItems && selectedInvoice.costItems.length > 0 && (
                      <div className="mb-6">
                        <h4 className="font-medium text-gray-900 mb-3">Cost Items (Internal Tracking)</h4>
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                          <p className="text-sm text-blue-800">
                            These are internal cost tracking items that won't appear on the PDF.
                          </p>
                        </div>
                        <div className="overflow-x-auto">
                          <div className="space-y-4">
                            {selectedInvoice.costItems.map((item, index) => (
                              <div key={item.id} className="border border-gray-200 rounded-lg p-4">
                                <div className="flex items-center justify-between mb-3">
                                  <h4 className="text-sm font-medium text-gray-700">Cost Item {index + 1}</h4>
                                  <span className="text-xs text-gray-500">{item.currency}</span>
                                </div>
                                
                                <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                                  <div className="md:col-span-2">
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
                                    <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-sm">
                                      {item.description}
                                    </div>
                                  </div>
                                  
                                  <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Quantity</label>
                                    <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-sm">
                                      {item.quantity}
                                    </div>
                                  </div>
                                  
                                  <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Rate</label>
                                    <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-sm">
                                      {formatCurrencyWithConversion(item.rate, item.currency, selectedInvoice.exchangeRate)}
                                    </div>
                                  </div>
                                  
                                  <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Total Cost</label>
                                    <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-sm font-medium">
                                      {formatCurrencyWithConversion(item.amount, item.currency, selectedInvoice.exchangeRate)}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Sale Items */}
                    <div className="mb-6">
                      <h4 className="font-medium text-gray-900 mb-3">Sale Items (Customer-Facing)</h4>
                      <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
                        <p className="text-sm text-green-800">
                          These items will appear on the PDF and in ledgers. You can edit them when clicking "Amend".
                        </p>
                      </div>
                      <div className="overflow-x-auto">
                        <div className="space-y-4">
                          {(selectedInvoice.saleItems || []).map((item, index) => (
                            <div key={item.id} className="border border-gray-200 rounded-lg p-4">
                              <div className="flex items-center justify-between mb-3">
                                <h4 className="text-sm font-medium text-gray-700">Sale Item {index + 1}</h4>
                                <span className="text-xs text-gray-500">{item.currency}</span>
                              </div>
                              
                              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                                <div className="md:col-span-2">
                                  <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
                                  <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-sm">
                                    {item.description}
                                  </div>
                                </div>
                                
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">Quantity *</label>
                                  <input
                                    type="number"
                                    min="1"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                                    value={item.quantity}
                                    onChange={(e) => {
                                      const newQuantity = parseInt(e.target.value) || 1;
                                      const itemsToUpdate = selectedInvoice.saleItems || [];
                                      const updatedItems = [...itemsToUpdate];
                                      updatedItems[index] = {
                                        ...item,
                                        quantity: newQuantity,
                                        amount: mulMoney(newQuantity, item.rate)
                                      };
                                      
                                      if (selectedInvoice.saleItems) {
                                        setSelectedInvoice({
                                          ...selectedInvoice,
                                          saleItems: updatedItems as SaleItem[]
                                        });
                                      } else {
                                        setSelectedInvoice({
                                          ...selectedInvoice,
                                          items: updatedItems
                                        });
                                      }
                                    }}
                                    placeholder="1"
                                  />
                                </div>
                                
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">Rate *</label>
                                  <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                                    value={item.rate}
                                    onChange={(e) => {
                                      const newRate = roundMoney(parseFloat(e.target.value) || 0);
                                      const itemsToUpdate = selectedInvoice.saleItems || [];
                                      const updatedItems = [...itemsToUpdate];
                                      updatedItems[index] = {
                                        ...item,
                                        rate: newRate,
                                        amount: mulMoney(item.quantity, newRate)
                                      };
                                      
                                      if (selectedInvoice.saleItems) {
                                        setSelectedInvoice({
                                          ...selectedInvoice,
                                          saleItems: updatedItems as SaleItem[]
                                        });
                                      } else {
                                        setSelectedInvoice({
                                          ...selectedInvoice,
                                          items: updatedItems
                                        });
                                      }
                                    }}
                                    placeholder="0.00"
                                  />
                                </div>
                                
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">Total Amount</label>
                                  <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-sm font-medium">
                                    {formatCurrencyWithConversion(item.amount, item.currency, selectedInvoice.exchangeRate)}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Summary */}
                    <div className="mb-6">
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <div className="flex justify-between items-center">
                          <div className="space-y-1">
                            <p className="text-sm text-gray-600">Sale Subtotal: {selectedInvoice.currency} {realtimeTotals.subtotal.toFixed(2)}</p>
                            <p className="text-sm text-blue-600">Cost Total: {selectedInvoice.currency} {realtimeTotals.costTotal.toFixed(2)}</p>
                            <p className="text-sm font-bold text-green-600">Profit: {selectedInvoice.currency} {realtimeTotals.profit.toFixed(2)}</p>
                            <p className="text-sm text-gray-600">Deduction: {selectedInvoice.currency} {selectedInvoice.discount.toFixed(2)}</p>
                            <p className="text-lg font-bold text-gray-900 border-t pt-1">Final Total: {selectedInvoice.currency} {realtimeTotals.total.toFixed(2)}</p>
                            {realtimeTotals.subtotal > 0 && (
                              <p className="text-xs text-green-600 font-medium">
                                Profit margin: {((realtimeTotals.profit / realtimeTotals.subtotal) * 100).toFixed(1)}%
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex space-x-4">
                      <button
                        onClick={() => handleApprove(selectedInvoice)}
                        disabled={isProcessing}
                        className="flex-1 px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
                      >
                        {isProcessing ? 'Processing...' : 'Approve'}
                      </button>
                      <button
                        onClick={() => handleReject(selectedInvoice)}
                        disabled={isProcessing}
                        className="flex-1 px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                      >
                        {isProcessing ? 'Processing...' : 'Reject'}
                      </button>
                      <button
                        onClick={() => handleAmend(selectedInvoice)}
                        disabled={isProcessing}
                        className="flex-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                      >
                        {isProcessing ? 'Processing...' : 'Amend'}
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-white shadow rounded-lg p-8 text-center">
                  <div className="text-gray-400 mb-4">
                    <svg className="mx-auto h-12 w-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Select an Invoice</h3>
                  <p className="text-gray-600">Choose an invoice from the list to view details and take action.</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PendingApprovals;
